import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, EMPTY } from 'rxjs';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';
import { IntakeService } from '../services/intake.service';
import { SurveyRunStateService } from '../services/survey-run-state.service';
import { environment } from '../../environments/environment';

/**
 * Intake route: creates a survey run and redirects to survey view.
 * Query params: surveyTypeId or surveyVersionId.
 */
@Component({
  selector: 'app-intake-create',
  standalone: true,
  template: `
    <div class="survey-container" role="status" aria-live="polite">
      @if (errorMessage) {
        <p class="survey-form__error" role="alert">{{ errorMessage }}</p>
      }
      @if (loading) {
        <p>Creating survey session...</p>
      }
    </div>
  `,
  styles: [':host { display: block; }']
})
export class IntakeCreateComponent implements OnInit, OnDestroy {
  loading = true;
  errorMessage: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly intake: IntakeService,
    private readonly runState: SurveyRunStateService
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        switchMap((params) => {
          let versionId = this.parsePositiveInt(params['surveyVersionId']);
          let typeId = this.parsePositiveInt(params['surveyTypeId']);
          if (versionId === undefined && typeId === undefined && environment.defaultSurveyTypeId != null) {
            typeId = environment.defaultSurveyTypeId;
          }
          if (versionId === undefined && typeId === undefined) {
            this.loading = false;
            this.errorMessage = 'Missing surveyTypeId or surveyVersionId in URL.';
            return EMPTY;
          }
          if (params['surveyVersionId'] != null && params['surveyVersionId'] !== '' && versionId === undefined) {
            this.loading = false;
            this.errorMessage = 'Invalid surveyVersionId in URL. Use a positive whole number.';
            return EMPTY;
          }
          if (params['surveyTypeId'] != null && params['surveyTypeId'] !== '' && typeId === undefined) {
            this.loading = false;
            this.errorMessage = 'Invalid surveyTypeId in URL. Use a positive whole number.';
            return EMPTY;
          }
          return this.intake.createRun(versionId, typeId);
        }),
        catchError((err: Error) => {
          this.loading = false;
          this.errorMessage = err.message ?? 'Failed to start osaf_survey.';
          return EMPTY;
        })
      )
      .subscribe((res) => {
        if (!res) return;
        this.runState.setRun(res.surveyRunId, res.token, res.expiresAtUtc);
        this.router.navigate(['/survey', res.surveyRunId], { replaceUrl: true });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Returns a positive integer when the value is valid; undefined when absent or invalid. */
  private parsePositiveInt(value: string | undefined): number | undefined {
    if (value == null || value === '') return undefined;
    const n = parseInt(value, 10);
    return Number.isInteger(n) && n >= 1 ? n : undefined;
  }
}
